'use client'

import { completeOnboarding } from '@/app/onboarding/_actions'
import { firstTimeLoginFormSchema } from '@/lib/schemas'
import { getStripe } from '@/lib/stripe/client'
import { calculateTrialEndUnixTimestamp } from '@/lib/stripe/helpers'
import { checkoutWithStripe } from '@/lib/stripe/server'
import { useClerk, useUser } from '@clerk/nextjs'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '../card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../form'
import SubmitButton from '../forms/SubmitButton'
import { Input } from '../input'
import { LogoWhite } from '../Logo'
import ThemeSetter from '../ThemeSetter'
import TimezoneSelect from '../timezone-select'
import { Tooltip, TooltipContent, TooltipTrigger } from '../tooltip'

export default function FirstTimeLoginDialog() {
  const [loading, setLoading] = useState(false)
  const params = useSearchParams()
  const { user } = useUser()
  const { signOut } = useClerk()
  const sessionId = params.get('session_id')
  const [showFirstTimeLogin, setShowFirstTimeLogin] = useState(true)

  const name = `${user?.firstName} ${user?.lastName}`
  const form = useForm<z.infer<typeof firstTimeLoginFormSchema>>({
    resolver: zodResolver(firstTimeLoginFormSchema),
    defaultValues: {
      name,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  })

  useEffect(() => {
    form.setValue('name', `${user?.firstName} ${user?.lastName}`)
  }, [name])

  const onSubmit: SubmitHandler<
    z.infer<typeof firstTimeLoginFormSchema>
  > = async (data) => {
    setLoading(true)
    // Convert `data` to FormData
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value.toString())
    })

    const res = await completeOnboarding(formData)
    if (res?.message) {
      // Reloads the user's data from the Clerk API
      await user?.reload()
    }
    if (res?.error) {
    }

    const { errorRedirect, sessionId } = await checkoutWithStripe(
      {
        type: 'recurring',
        id: process.env.NEXT_PUBLIC_STRIPE_SUBSCRIPTION_PRICE_ID ?? '',
        trial_period_days: 30,
      },
      '/home',
      'if_required',
      {
        trial_end: calculateTrialEndUnixTimestamp(30),
        trial_settings: {
          end_behavior: {
            missing_payment_method: 'pause',
          },
        },
      },
    )

    if (sessionId) {
      const session = await getStripe()
      session?.redirectToCheckout({ sessionId })
    }
    // Add logic for handling the form submission here
    setLoading(false)
  }

  return (
    <div className="grid h-screen grid-cols-1 md:grid-cols-2">
      <div className="hidden h-full flex-col items-center bg-primary p-16 md:flex">
        <div className="flex h-full flex-1 items-center">
          {' '}
          <div className="mb-64 flex flex-col gap-4">
            <div className="flex items-center">
              <Tooltip>
                <TooltipTrigger>
                  <ArrowLeft
                    size={18}
                    onClick={() => {
                      signOut()
                    }}
                    className="flex-shrink-0 transform cursor-pointer text-white transition-transform duration-300 hover:translate-x-[-4px]"
                  />
                </TooltipTrigger>
                <TooltipContent>Sign Out</TooltipContent>
              </Tooltip>

              <LogoWhite />
            </div>

            <div className="flex flex-col">
              <span className="text-[38px] font-bold text-white">
                Cadence Calendar
              </span>
              <span className="text-[18px] text-white/70">
                30 days free trial
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="relative flex h-full flex-col">
        <div className="absolute right-4 top-4">
          <ThemeSetter />
        </div>
        {showFirstTimeLogin && (
          <Card className="flex h-full flex-1 items-center rounded-none">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="">
                <CardHeader className="pb-0">
                  <CardTitle>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <img
                          src={user?.imageUrl}
                          width={40}
                          className="rounded-full"
                        />
                        {user && user.firstName
                          ? `Hi, ${user.firstName}!`
                          : `Hello!`}
                      </div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-w-lg space-y-6">
                  <div className="flex flex-col space-y-2.5">
                    <span>
                      Welcome to{' '}
                      <span className="font-bold">Cadence Calendar</span>!
                    </span>
                    <span>
                      We're delighted to have you on board. We've built Cadence
                      Calendar to help you stay organized, track transactions,
                      and streamline your teaching calendar to skyrocket your
                      productivity using a single all-in-one solution.
                    </span>
                    <span>
                      During your{' '}
                      <span className="font-bold">30-day free trial</span>, you
                      will have access to all of the features Cadence Calendar
                      offers.
                    </span>
                  </div>

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Timezone</FormLabel>
                        <FormControl>
                          <TimezoneSelect field={field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <SubmitButton loading={loading} buttonText="Continue" />
                </CardContent>
              </form>
            </Form>
          </Card>
        )}
      </div>
    </div>
  )
}
